/**
 * API configuration per deployment stage.
 *
 * The middleware (HILTBusinessReportingService) exposes getMetrics (and
 * later getWeekNumbers) through API Gateway. Auth is Midway: the browser
 * sends its Midway cookie and the middleware resolves the caller identity.
 *
 * Each stage's endpoint is the service's custom Midway API domain
 * (`api-<stage>.reporting.agids.amazon.dev`) — the domain fronted by the ALI
 * Midway authorizer (which serves `/sso/login`) and for which Midway issues
 * cookies. SentryFetch drives its MCS auth flow against this origin.
 */

export interface ApiStageConfig {
  endpoint: string;
  region: string;
}

const STAGE_CONFIGS: Record<string, ApiStageConfig> = {
  prod: {
    endpoint: 'https://api-prod.reporting.agids.amazon.dev',
    region: 'us-east-1',
  },
  gamma: {
    endpoint: 'https://api-gamma.reporting.agids.amazon.dev',
    region: 'us-east-1',
  },
  dev: {
    endpoint: 'https://api-dev.reporting.agids.amazon.dev',
    region: 'us-west-2',
  },
};

/**
 * Parent zone of the custom (non-Mons) domain. Every stage is served from a
 * labelled child zone — `dev.`, `gamma.`, `prod.` — and the apex holds only the
 * NS delegations to them, so nothing is served there today.
 */
const CUSTOM_DOMAIN_PARENT = 'reporting.agids.amazon.dev';

/**
 * Stage from the deployed hostname, most-specific check first.
 *
 * Two families of deployed hostname exist:
 * - Mons: `static-assets.<stage-cell>.[<prefix>-]hiltbusinessrepor.mons-app.amazon.dev`,
 *   carrying the stage in the stage segment (dev-us-west-2 / gamma-us-east-1 /
 *   prod-us-east-1) and, for non-prod, optionally in the domain prefix
 *   (development- / staging-).
 * - Custom: `dev.` / `gamma.` / `prod.` prefixed under
 *   `reporting.agids.amazon.dev`, where the leading label is the stage.
 *
 * NOTE: never use a bare 'dev' substring check — the custom domain ends in
 * ".dev", and its dev stage is the more specific `dev.` prefix.
 */
function detectStage(): string {
  const hostname = window.location.hostname;

  // The apex is not served today — prod uses the `prod.` label like the other
  // stages. It is mapped to prod anyway because the apex is only ever intended
  // for prod (the shape customer-hub.ads.a2z.com uses), so if prod moves there
  // this file needs no change. Leaving it to fall through would instead point
  // the prod UI at the dev API.
  if (hostname === CUSTOM_DOMAIN_PARENT) {
    return 'prod';
  }

  // Custom domain: the leading label is the stage. Checked by exact match so it
  // cannot be confused with the ".dev" TLD, and so an unknown subdomain falls
  // through rather than silently resolving to a wrong stage.
  if (hostname.endsWith(`.${CUSTOM_DOMAIN_PARENT}`)) {
    const stage = hostname.slice(0, -`.${CUSTOM_DOMAIN_PARENT}`.length);
    if (stage === 'prod' || stage === 'gamma' || stage === 'dev') {
      return stage;
    }
  }

  // Non-prod stages first, so a stage marker always wins over the
  // deployed-domain catch-all below.
  if (hostname.includes('gamma') || hostname.includes('staging')) {
    return 'gamma';
  }
  if (
    hostname.includes('dev-us-west-2') ||
    hostname.includes('development') ||
    hostname.includes('beta')
  ) {
    return 'dev';
  }
  // Deployed Mons domain with no non-prod marker → prod.
  if (hostname.includes('hiltbusinessrepor')) {
    return 'prod';
  }
  // localhost / 0.0.0.0 / anything unrecognized → dev.
  return 'dev';
}

export function getApiConfig(): ApiStageConfig {
  return STAGE_CONFIGS[detectStage()] || STAGE_CONFIGS.dev;
}
